/**
 * The left rail: workspace button, collapsible nav groups, live counts and the
 * control that narrows it to icons. Hiding the rail outright is the top bar's
 * job — this button only switches between the wide and the icon-only widths.
 *
 * Group open/closed state is persisted in settings, so the sidebar looks the
 * same next time. When the rail is collapsed the groups always render — the
 * labels hide, but the icons stay reachable.
 */
import { NavLink } from 'react-router-dom';
import { NAV } from '../constants/defaults.js';
import { useApp } from '../store/AppContext.jsx';
import Icon from '../lib/icons.jsx';
import { dealsOf } from '../domain/crm.js';
import WorkspaceMenu from './WorkspaceMenu.jsx';

/**
 * Split the flat NAV list into [{ label, items }].
 *
 * Entries marked `hidden` keep their place in NAV — the router and the page
 * header still read them — they just do not appear in the sidebar. A group
 * whose items are all hidden is dropped rather than left as a bare heading.
 */
/**
 * Nav paths that are a prefix of another nav path.
 *
 * NavLink counts a link as active when the current URL merely starts with its
 * `to`, so /url-tracking stays lit on /url-tracking/campaigns and two items
 * glow at once. `end` turns that off — but only these items may have it.
 *
 * The distinction is whether the child is a menu of its own. /settings has no
 * sibling entries below it: /settings/profile is a pane of the same screen, so
 * the Settings item should stay lit there and must not get `end`. /marketing
 * and /url-tracking do have children in this list, so each of them highlights
 * only its own page. Working it out from NAV rather than listing the paths by
 * hand means a menu added later behaves correctly without anybody remembering
 * this rule.
 */
const EXACT_ONLY = new Set(
  NAV.filter(e => e.path && NAV.some(o => o.path && o.path !== e.path && o.path.startsWith(`${e.path}/`)))
    .map(e => e.path)
);

function groupNav(can) {
  const groups = [];
  NAV.forEach(entry => {
    if (entry.g) groups.push({ label: entry.g, items: [] });
    /* A menu the role cannot open is not shown. The API refuses it as well —
       this only avoids offering a door that will not open.

       `perm` overrides which menu and verb are checked, for the entries that
       are an action on another menu rather than a screen of their own. */
    else if (groups.length && !entry.hidden) {
      const [menu, verb] = Array.isArray(entry.perm)
        ? entry.perm
        : [entry.perm || entry.v, 'view'];
      if (can(menu, verb)) groups[groups.length - 1].items.push(entry);
    }
  });
  return groups.filter(g => g.items.length);
}

/*
 * Two kinds of number live on this rail and they must not look alike.
 *
 * A grey badge is a size: how many rows the page holds, and it will equal
 * the count on the page when you get there. A red one is a nudge: how many
 * of them are asking for something, which is deliberately smaller than the
 * page and goes down as the work gets done.
 *
 * Activities sat between the two - red's meaning, grey's clothes - so the
 * rail said 122 and the page said 298 with nothing to explain the gap. It
 * is a nudge, so it is red.
 */
const HOT = new Set(['queue', 'follow-ups', 'activities']);

/** What each red number is counting, for the row's own tooltip. */
const NUDGE = {
  queue: 'unread',
  'follow-ups': 'not started',
  activities: 'still to do'
};

export default function Sidebar() {
  const {
    S, stats, menuCounts, pipeline, leads, outboxCount, crm, demos, patchSettings,
    miniNav, setMiniNav, setNavHidden, setFlag, clearSelection, can, menusReady
  } = useApp();

  /* What nobody has picked up yet — and only that. A badge is a nudge, and
     a follow-up somebody is already working is not something to be nudged
     about; counting those would leave a number on the menu that never went
     down. Read off the same list the page reads, so a demo handed across a
     minute ago shows without the page being opened. */
  const waiting = (demos?.bookings || [])
    .filter(b => b.session && b.session.followStatus === 'handed').length;

  const counts = {
    /* The board is what is still being worked; Deals is what has been won. A
       row is on one or the other, so the badges cannot overlap. */
    pipeline: leads.open,
    deals: dealsOf(crm).length,
    contacts: crm.contacts.length,
    companies: crm.companies.length,
    activities: pipeline.acts,
    /* Each of these four counts what its own page lists — same rule as the
       page, worked out once in the store and read here. Running it in this
       render was four walks of the whole book per keystroke. */
    clients: menuCounts.clients,
    trials: menuCounts.trials,
    payments: menuCounts.payments,
    renewals: menuCounts.renewals,
    queue: stats.open,
    outbox: outboxCount,
    'follow-ups': waiting
  };

  const navOpen = S.navOpen || {};
  const toggleGroup = label =>
    patchSettings({ navOpen: { ...navOpen, [label]: navOpen[label] === false } });

  /** Leaving a page resets the stat-tile filter and any row selection. */
  const onNavigate = () => {
    setFlag('');
    clearSelection();
    if (window.innerWidth < 820) setNavHidden(true);
    window.scrollTo(0, 0);
  };

  return (
    <aside className="side">
      <WorkspaceMenu />

      {/* The collapse control sits at the top, under the brand, so it is in
          reach without scrolling to the foot of a long rail. */}
      <button
        type="button"
        className="railtoggle top"
        aria-label={miniNav ? 'Expand the menu' : 'Collapse the menu'}
        title={miniNav ? 'Expand the menu' : 'Collapse the menu'}
        onClick={() => setMiniNav(m => !m)}
      >
        <span className={`railchev${miniNav ? ' back' : ''}`}><Icon name="chev" /></span>
        <span className="navtxt">Collapse</span>
      </button>

      <div className="navscroll">
        {/*
          * Nothing until the policy is known.
          *
          * `can()` answers yes while it is unknown, which is right for a
          * button the server will refuse anyway and wrong here: the rail
          * drew every module for the moment before /me landed and then took
          * most of them away, so somebody with only the Clients module
          * watched Sales appear and vanish on every load. A quiet shape for
          * a fraction of a second is honest; the wrong rail is not.
          */}
        {!menusReady ? (
          <div className="navwait" aria-hidden="true">
            {[0, 1, 2, 3, 4, 5].map(i => <span key={i} />)}
          </div>
        ) : groupNav(can).map(group => {
          const open = navOpen[group.label] !== false;
          const total = group.items.reduce((t, x) => t + (counts[x.v] || 0), 0);

          return (
            <div className="navgroup" key={group.label}>
              <button type="button" className="navlabel" onClick={() => toggleGroup(group.label)}>
                <span>{group.label}</span>
                {!open && total ? <span className="gcount">{total}</span> : null}
                <span className={`gchev${open ? ' up' : ''}`}><Icon name="chev" /></span>
              </button>

              <div className={`navitems${open ? '' : ' hide'}`}>
                {group.items.map(item => {
                  const count = counts[item.v];
                  return (
                    <NavLink
                      key={item.v}
                      to={item.path}
                      /* A red badge is a subset of the page it points at, so
                         the row says which subset rather than leaving the
                         difference to be discovered. */
                      title={count && HOT.has(item.v)
                        ? `${item.n} — ${count} ${NUDGE[item.v] || 'waiting'}`
                        : item.n}
                      end={EXACT_ONLY.has(item.path)}
                      onClick={onNavigate}
                      className={({ isActive }) => `nav${isActive ? ' on' : ''}`}
                    >
                      <div className={`ic ${item.c}`}><Icon name={item.i} solid /></div>
                      <span className="navtxt">{item.n}</span>
                      {/* Red is for a count that is asking for something: the
                          alert queue, and demos waiting to be picked up.
                          Everything else is a size, not a nudge. */}
                      {count ? (
                        <span className={`badge${HOT.has(item.v) ? ' hot' : ''}`}>{count}</span>
                      ) : null}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
}
